import { ChangeEvent, FC, useState } from 'react';

import { DatePicker, Form, Input, message, Modal, Select, Spin } from 'antd';
import { Rule, RuleObject } from 'antd/lib/form';
import { useDispatch } from 'react-redux';

import { currencyOptions, timezoneOptions } from './constants';
import { ISchoolFormData } from '../../../../app/providers/redux/slices/currentSchool/types';
import { TAppDispatch } from '../../../../app/providers/redux/store';
import { optionsRegMetki } from '../../../../helpers/utils/data';
import {
  showErrorMessage,
  showFormErrors,
  showSuccessNotification,
} from '../../../../helpers/utils/ui';
import { validateDomain } from '../../../../helpers/utils/validators';
import { schoolApi } from '../../../../shared/common/api/endpoints/schoolApi';
import { handleResponseError } from '../../../../shared/common/api/middleware';
import Loading from '../../../../shared/common/components/reports/cohort/components/Loading';
import { routeNames } from '../../../../shared/common/constants/routes';
import { normalizeSpaces } from '../utils/normalize';
import { requiredIfUtmOrigin } from '../utils/schoolValidator';
import moment from 'moment';

interface IAddModalProps {
  isVisible: boolean;
  setVisible: (val: boolean) => void;
}

const AddModal: FC<IAddModalProps> = ({ isVisible, setVisible }) => {
  const [ form ] = Form.useForm();
  const dispatch = useDispatch<TAppDispatch>();
  const [ isFetch, setIsFetch ] = useState(false);

  const requiredIfUtmOrigin = (form: any) => {
    return (rule: RuleObject, value: string | undefined, callback: (error?: string) => void) => {
      const utmOrigin = form.getFieldValue(['utm_settings', 'utm_origin']);
      if ((utmOrigin === 2 || utmOrigin === 3) && !value) {
        callback('UTM source обязателен для заполнения при utm_origin равном 2 или 3');
      } else {
        callback();
      }
    };
  };
    const validateDateFormat = (_: Rule, value: string | undefined): Promise<void> => {
    const regex = /^\d{2}\.\d{2}\.\d{4}$/;

    if (value && !regex.test(value)) {
      return Promise.reject('Неверный формат даты. Используйте формат ДД.ММ.ГГГГ');
    }
    return Promise.resolve();
  };
  const addSchool = (values: ISchoolFormData) => {
    try {
      setIsFetch(true);

      const domainValue = values.domain.includes('.getcourse')
        ? values.domain
        : `${values.domain}.getcourse.ru`;

      const modifiedValues = { ...values, domain: domainValue };
      Object.entries(modifiedValues).forEach(([key, value]) => {
        if (typeof value === 'string') {
          if (value.startsWith('http')) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            values[key] = value.replace(/(^\w+:|^)\/\//, '');
          }
        }
      });
      schoolApi
        .addSchool(modifiedValues)
        .then((response) => {
          if (response.status === 200) {
            showSuccessNotification('Школа успешно добавлена.');
            window.location.href = routeNames.schoolAdded;
          }
        })
        .catch((err) => {
          const errorMessage = JSON.stringify(err.response.data.message) || 'Произошла неизвестная ошибка';
          message.error(errorMessage, 3);
          handleResponseError(dispatch, err);
          if (err.response?.status === 400) {
            showFormErrors(form, err.response.data);
            return;
          }
        })
        .finally(() => setIsFetch(false));
    } catch (err) {
      showErrorMessage('Произошла ошибка, повторите попытку позже.');
    }
  };

  const handleDomainChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputDomain = e.target.value;
    let cleanedDomain = inputDomain;

    cleanedDomain = cleanedDomain.replace(/(https?:\/\/)?(www\.)?/i, '');

    form.setFieldsValue({ domain: cleanedDomain });
  };

  const utmOrigin = Form.useWatch([ 'utm_settings', 'utm_origin' ], form);

  const showUtmFields = utmOrigin === 2 || utmOrigin === 3;
  const initialDate = moment().subtract(5, 'years').format('DD.MM.YYYY');
  const [dateValue, setDateValue] = useState(initialDate);
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDateValue(e.target.value);
  };
 const validateDateField = (rule: Rule, value: string | undefined) => {
    if (!value) {
      return Promise.reject('Поле обязательное');
    }
    return Promise.resolve();
  };
  return (
    <Modal
      title="Добавление школы, созданной на платформе GetCourse"
      open={isVisible}
      onOk={() => form.submit()}
      onCancel={() => {
        form.resetFields();
        setIsFetch(false);
        setVisible(false);
      }}
      okText={'Добавить'}
      cancelText="Отмена"
      destroyOnClose
      okButtonProps={{ loading: isFetch }}
    >
      <Loading loading={isFetch} hasTip={false}>
        <Form
          form={form}
          onFinish={addSchool}
          layout="vertical"
          requiredMark={false}
          initialValues={{
            currency: 1,
            timezone: '74',
            language: 1,
            utm_settings: {
              utm_origin: 1,
              utm_source: '',
              utm_medium: '',
              utm_campaign: '',
              utm_content: '',
              utm_term: '',
              utm_group: '',
            },
          }}
        >
          <Form.Item
            label="Название школы"
            name="name"
            rules={[ { required: true, message: 'Поле обязательное' } ]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Валюта" name="currency">
            <Select>
              {currencyOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Часовой пояс" name="timezone">
            <Select>
              {timezoneOptions.map((option) => (
                <Select.Option key={option.value} value={option.value} data-select2-id={option.value + 16}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Язык интерфейса" name="language">
            <Select>
              <Select.Option value={1}>Русский</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Адрес аккаунта GetCourse (sait.getcourse.ru - в настройках)"
            name="domain"
            rules={[
              {
                required: true,
                message: 'Поле обязательное',
              },
              {
                validator: validateDomain,
              },
            ]}
            help={
              form.getFieldError('domain')
                ? form.getFieldError('domain')[0]
                : ''
            }
          >
            <Input
              onChange={handleDomainChange}
              // suffix={domainSuffix}
              placeholder="В формате name.getcourse.ru"
            />
          </Form.Item>

          <Form.Item
            label="Привязанный домен к GetCourse, если есть"
            name="main_domain"
            rules={[

              {
                validator: validateDomain,
              },
            ]}
            help={
              form.getFieldError('domain')
                ? form.getFieldError('domain')[0]
                : ''
            }
          >
            <Input />
          </Form.Item>
          <Form.Item
      label="Дата первой регистрации пользователя в вашей базе."
      name="first_registration_date"
      rules={[
        { required: true, message: 'Поле обязательное' },
        { validator: validateDateField }, 
        { validator: validateDateFormat }, 
      ]}
      style={{ width: '100%' }}
      initialValue={initialDate} 
    >
      <Input
        style={{ width: '100%' }}
        placeholder="ДД.ММ.ГГГГ"
        value={dateValue} 
        onChange={handleChange} 
      />
    </Form.Item>


          <Form.Item
            label="Getcourse API Key"
            name="api_key"
            rules={[ { required: true, message: 'Поле обязательное' } ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Откуда брать UTM метки регистраций:"
            name={[ 'utm_settings', 'utm_origin' ]}
            rules={[ { required: true, message: 'Поле обязательное' } ]}
          >
            <Select>
              {optionsRegMetki.map((el) => (
                <Select.Option value={el.id} key={el.id}>
                  {el.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>


          {showUtmFields && (
            <>
              <Form.Item
                label="Название доп. поля для метки utm_source"
                name={[ 'utm_settings', 'utm_source' ]}
                normalize={normalizeSpaces}
                dependencies={[ 'utm_settings.utm_origin' ]}
                rules={[
                  { validator: requiredIfUtmOrigin(form) }
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name={[ 'utm_settings', 'utm_medium' ]}
                label="Название доп. поля для метки utm_medium"
                normalize={normalizeSpaces}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name={[ 'utm_settings', 'utm_campaign' ]}
                label="Название доп. поля для метки utm_campaign"
                normalize={normalizeSpaces}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name={[ 'utm_settings', 'utm_content' ]}
                label="Название доп. поля для метки utm_content"
                normalize={normalizeSpaces}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name={[ 'utm_settings', 'utm_term' ]}
                label="Название доп. поля для метки utm_term"
                normalize={normalizeSpaces}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name={[ 'utm_settings', 'utm_group' ]}
                label="Название доп. поля для метки utm_group"
                normalize={normalizeSpaces}
              >
                <Input />
              </Form.Item>
            </>
          )}

        </Form>
      </Loading>
    </Modal>
  );
};

export default AddModal;
